import { useMemo, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";
import { Zap } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import type { AuditLogFilter } from "@/features/admin/api/dto";
import { useAuditLogs } from "@/features/admin/api/use-audit-logs";
import {
  AuditLogsFilters,
  auditLogsColumns,
  SimulateAuditEventDialog,
  type AuditLogsFiltersValue,
} from "@/features/admin/components";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";

/**
 * Journal d'audit consolidé paginé (`POST /AuditLogs/all`, guide §6.8) avec
 * barre de filtres externe. ⚙️ `simulate-audit-event` : action discrète en
 * haut de page, réservée à flashpay-superadmin (visibilité via RequireRole).
 * Rôles page : flashpay-superadmin, flashpay-admin (garde dans le router).
 */
export function AuditLogsPage() {
  const { t } = useAdminTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [filters, setFilters] = useState<AuditLogsFiltersValue>({});
  const [simulateOpen, setSimulateOpen] = useState(false);

  const params = useMemo(
    () => ({
      filter: {
        sourceService: filters.sourceService,
        resourceType: filters.resourceType,
        resourceId: filters.resourceId,
        actorKeycloakId: filters.actorKeycloakId,
      } satisfies AuditLogFilter,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [filters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useAuditLogs(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-foreground">
            {t("admin.audit.title")}
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {t("admin.audit.subtitle")}
          </p>
        </div>
        <RequireRole roles={[FLASHPAY_ROLES.SUPERADMIN]}>
          <Button variant="outline" onClick={() => setSimulateOpen(true)}>
            <Zap className="mr-2 h-4 w-4" />
            {t("admin.audit.simulate.button")}
          </Button>
        </RequireRole>
      </div>

      <AuditLogsFilters
        value={filters}
        onChange={(value) => {
          setFilters(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />

      <DataTableClar
        data={data?.items ?? []}
        columnsClar={auditLogsColumns}
        title={t("admin.audit.title")}
        exportFileName="audit-logs"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <SimulateAuditEventDialog
        open={simulateOpen}
        onOpenChange={setSimulateOpen}
      />
    </section>
  );
}
